import type { Activity, Company, Contact, Opportunity, Proposal } from "../types";
import { lastActivityDate } from "./businessRules";

export function companyOpportunities(companyId: string, opportunities: Opportunity[]): Opportunity[] {
  return opportunities.filter((o) => o.companyId === companyId);
}

export function companyContacts(companyId: string, contacts: Contact[]): Contact[] {
  return contacts.filter((c) => c.companyId === companyId);
}

export function openOpportunitiesCount(companyId: string, opportunities: Opportunity[]): number {
  return companyOpportunities(companyId, opportunities).filter((o) => o.stage !== "Won" && o.stage !== "Lost").length;
}

export function totalPipelineValue(companyId: string, opportunities: Opportunity[]): number {
  return companyOpportunities(companyId, opportunities)
    .filter((o) => o.stage !== "Won" && o.stage !== "Lost")
    .reduce((sum, o) => sum + (o.value ?? 0), 0);
}

export function totalWonRevenue(companyId: string, opportunities: Opportunity[]): number {
  return companyOpportunities(companyId, opportunities)
    .filter((o) => o.stage === "Won")
    .reduce((sum, o) => sum + (o.finalValue ?? o.value ?? 0), 0);
}

export function companyLastActivity(companyId: string, opportunities: Opportunity[], activities: Activity[]): string | undefined {
  const opps = companyOpportunities(companyId, opportunities);
  if (opps.length === 0) return undefined;
  return opps
    .map((o) => lastActivityDate(o.id, activities, o.createdAt))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
}

export function companyRecentActivities(companyId: string, opportunities: Opportunity[], activities: Activity[], limit = 5): Activity[] {
  const oppIds = new Set(companyOpportunities(companyId, opportunities).map((o) => o.id));
  return activities
    .filter((a) => oppIds.has(a.opportunityId))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, limit);
}

export function stageTotal(stage: Opportunity["stage"], opportunities: Opportunity[]): number {
  return opportunities.filter((o) => o.stage === stage).reduce((sum, o) => sum + (o.value ?? 0), 0);
}

export function proposalsFor(opportunityId: string, proposals: Proposal[]): Proposal[] {
  return proposals
    .filter((p) => p.opportunityId === opportunityId)
    .sort((a, b) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime());
}

export function activitiesFor(opportunityId: string, activities: Activity[]): Activity[] {
  return activities
    .filter((a) => a.opportunityId === opportunityId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
